import React from 'react';
import { Helmet } from 'react-helmet-async';
import { SITE_CONFIG, getFullTitle, getCanonicalUrl } from '../config/seoConfig';

/**
 * SEO Component - Manages meta tags, Open Graph, and JSON-LD structured data
 *
 * @param {string} title - Page title (will be appended with site name)
 * @param {string} description - Meta description
 * @param {string} path - URL path for canonical URL
 * @param {object} jsonLd - Optional JSON-LD structured data
 * @param {string} ogType - Open Graph type (default: 'website')
 */
const SEO = ({ title, description, path, jsonLd, ogType = 'website' }) => {
  const fullTitle = getFullTitle(title);
  const canonicalUrl = getCanonicalUrl(path);

  // Default Organization schema
  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_CONFIG.siteName,
    url: SITE_CONFIG.siteUrl,
    logo: `${SITE_CONFIG.siteUrl}/logo.png`,
  };

  // WebSite schema
  const webSiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_CONFIG.siteName,
    url: SITE_CONFIG.siteUrl,
  };

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph Tags */}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content={SITE_CONFIG.siteName} />
      <meta property="og:image" content={SITE_CONFIG.ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={SITE_CONFIG.ogImage} />

      {/* JSON-LD Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(webSiteSchema)}
      </script>
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
